import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import type { ApiResponse } from '@/types/api'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api'

/**
 * 백엔드가 LocalDateTime(Z 없음)으로 반환하는 타임스탬프에 "Z"를 붙여
 * JavaScript에서 UTC로 올바르게 파싱되도록 정규화
 */
const ISO_DATETIME_WITHOUT_TZ = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/

function normalizeTimestamps(data: unknown): unknown {
  if (typeof data === 'string' && ISO_DATETIME_WITHOUT_TZ.test(data)) {
    return data + 'Z'
  }
  if (Array.isArray(data)) {
    return data.map(normalizeTimestamps)
  }
  if (data && typeof data === 'object') {
    const result: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(data)) {
      result[key] = normalizeTimestamps(value)
    }
    return result
  }
  return data
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor - localStorage에서 토큰 읽어서 헤더에 추가
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    try {
      const authStorage = localStorage.getItem('admin-auth-storage')
      if (authStorage) {
        const parsed = JSON.parse(authStorage)
        const state = parsed?.state || parsed
        if (state?.accessToken) {
          config.headers.Authorization = `Bearer ${state.accessToken}`
        }
      }
    } catch {
      // localStorage 파싱 실패 시 무시 (토큰 없이 요청)
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Token refresh state (중복 갱신 방지)
let isRefreshing = false
let refreshSubscribers: Array<(token: string) => void> = []

function subscribeTokenRefresh(cb: (token: string) => void) {
  refreshSubscribers.push(cb)
}

function onTokenRefreshed(token: string) {
  refreshSubscribers.forEach((cb) => cb(token))
  refreshSubscribers = []
}

function onRefreshFailed() {
  refreshSubscribers = []
}

// Response interceptor - 타임스탬프 정규화 + 토큰 갱신 및 에러 처리
apiClient.interceptors.response.use(
  (response) => {
    if (response.data && response.config.responseType !== 'blob') {
      response.data = normalizeTimestamps(response.data)
    }
    return response
  },
  async (error: AxiosError<ApiResponse<unknown>>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }
    const url = originalRequest?.url

    // 403 Forbidden: 권한 없음 → 로그아웃하지 않고 에러만 전달
    // (비즈니스 로직의 ForbiddenException은 세션 무효가 아님)
    if (error.response?.status === 403) {
      return Promise.reject(error)
    }

    // 401 Unauthorized: 토큰 만료 → 갱신 시도
    if (error.response?.status === 401) {
      // /auth/me, /auth/refresh 요청 자체의 401은 그대로 전달 (무한 루프 방지)
      if (url && (url.includes('/auth/me') || url.includes('/auth/refresh'))) {
        return Promise.reject(error)
      }

      // 이미 retry한 요청이면 로그인 페이지로
      if (originalRequest._retry) {
        goToLogin()
        return Promise.reject(error)
      }

      // 이미 다른 요청이 갱신 중이면 큐에 등록하고 대기
      if (isRefreshing) {
        return new Promise((resolve) => {
          subscribeTokenRefresh((newToken: string) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`
            resolve(apiClient(originalRequest))
          })
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        const authStorage = localStorage.getItem('admin-auth-storage')
        const parsed = authStorage ? JSON.parse(authStorage) : null
        const state = parsed?.state || parsed
        const refreshToken = state?.refreshToken

        if (!refreshToken) {
          throw new Error('No refresh token')
        }

        const response = await apiClient.post('/auth/refresh', { refreshToken })
        const { accessToken: newAccessToken, refreshToken: newRefreshToken, user } = response.data.data

        // localStorage 업데이트
        const updatedState = { ...state, accessToken: newAccessToken, refreshToken: newRefreshToken, user }
        localStorage.setItem('admin-auth-storage', JSON.stringify({ state: updatedState }))

        isRefreshing = false
        onTokenRefreshed(newAccessToken)

        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
        return apiClient(originalRequest)
      } catch {
        isRefreshing = false
        onRefreshFailed()
        goToLogin()
        return Promise.reject(error)
      }
    }

    return Promise.reject(error)
  }
)

function goToLogin() {
  localStorage.removeItem('admin-auth-storage')
  if (window.location.pathname !== '/login') {
    window.location.href = '/login'
  }
}

// Helper functions
export const handleApiError = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const apiError = error.response?.data as ApiResponse<unknown>
    const info = apiError?.error
    // 필드 검증 실패는 "Validation failed"만으로는 무엇을 고칠지 알 수 없다. 필드별 사유를 덧붙인다.
    if (info?.code === 'VALIDATION_ERROR' && info.details && Object.keys(info.details).length > 0) {
      const fields = Object.entries(info.details)
        .map(([field, reason]) => `${field}: ${String(reason)}`)
        .join(', ')
      return `입력값 오류 — ${fields}`
    }
    return info?.message || error.message || 'An error occurred'
  }
  return 'An unexpected error occurred'
}
